import { DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import 'dayjs/locale/de.js';
import 'dayjs/locale/fr-ch.js';
import 'dayjs/locale/it-ch.js';
import 'dayjs/locale/en-gb.js';
import { SelectDropdownOptions } from '../models/select-dropdown-options.model';
import { LoggerService } from './logger.service';

export type AppLanguage = 'de' | 'fr' | 'it' | 'en';

export const APP_LANGUAGES: AppLanguage[] = ['de', 'fr', 'it', 'en'];

/** Fallback für alles, was nicht übersetzt ist oder keine (unterstützte) Sprache hat. */
export const DEFAULT_APP_LANGUAGE: AppLanguage = 'de';

/**
 * dayjs-Locale pro Sprache — liefert Wochentags- und Monatsnamen (`dddd`, `MMMM`, Datepicker).
 * `en-gb` statt `en`, weil die Woche sonst am Sonntag beginnt.
 */
export const DAYJS_LOCALE_BY_APP_LANGUAGE: Record<AppLanguage, string> = {
  de: 'de',
  fr: 'fr-ch',
  it: 'it-ch',
  en: 'en-gb',
};

/** Sprachnamen bewusst in der jeweiligen Sprache selbst (Endonyme) — man soll die eigene Sprache immer finden. */
export const APP_LANGUAGE_OPTIONS: SelectDropdownOptions[] = [
  { label: 'Deutsch', value: 'de' },
  { label: 'Français', value: 'fr' },
  { label: 'Italiano', value: 'it' },
  { label: 'English', value: 'en' },
];

/**
 * Schaltet die Oberflächensprache um (ngx-translate + `<html lang>`). Woher die Sprache kommt, entscheidet die App:
 * work-office aus dem Profil des Mitarbeiters, die SFG-App aus der Gerätesprache bzw. der Übersteuerung im Seitenmenü.
 *
 * Die zuletzt verwendete Sprache wird lokal gemerkt, damit die App beim nächsten Start sofort in der richtigen
 * Sprache erscheint und nicht erst nach dem Laden des Profils umspringt.
 */
@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private static readonly LAST_USED_STORAGE_KEY = 'work-language';

  private readonly translateService = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  public readonly currentLanguage = signal<AppLanguage>(DEFAULT_APP_LANGUAGE);

  /**
   * Bildet beliebige Sprachcodes (`fr`, `fr-CH`, `it_IT`, `EN`) auf eine unterstützte Sprache ab.
   * Liefert `null`, wenn die Sprache nicht unterstützt ist — der Aufrufer entscheidet über den Fallback.
   */
  public static toAppLanguage(languageCode: string | null | undefined): AppLanguage | null {
    const primaryCode = (languageCode ?? '').trim().toLowerCase().split(/[-_]/)[0];
    return APP_LANGUAGES.find((language) => language === primaryCode) ?? null;
  }

  /** Registriert die Sprachen und setzt die zuletzt verwendete Sprache (oder Deutsch). */
  public async initialize(): Promise<void> {
    this.translateService.addLangs(APP_LANGUAGES);
    this.translateService.setFallbackLang(DEFAULT_APP_LANGUAGE);
    await this.use(this.readStorage(LanguageService.LAST_USED_STORAGE_KEY) ?? DEFAULT_APP_LANGUAGE);
  }

  public async use(language: AppLanguage | null | undefined): Promise<void> {
    const appLanguage = LanguageService.toAppLanguage(language) ?? DEFAULT_APP_LANGUAGE;
    this.currentLanguage.set(appLanguage);
    dayjs.locale(DAYJS_LOCALE_BY_APP_LANGUAGE[appLanguage]);
    this.document.documentElement.lang = appLanguage;
    this.writeStorage(LanguageService.LAST_USED_STORAGE_KEY, appLanguage);
    try {
      await lastValueFrom(this.translateService.use(appLanguage));
    } catch (useLanguageException) {
      LoggerService.ERROR(this, 'useLanguageException', useLanguageException);
    }
  }

  public readStorage(storageKey: string): AppLanguage | null {
    try {
      return LanguageService.toAppLanguage(localStorage.getItem(storageKey));
    } catch (storageException) {
      return null;
    }
  }

  public writeStorage(storageKey: string, language: AppLanguage | null): void {
    try {
      if (language) {
        localStorage.setItem(storageKey, language);
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (storageException) {
      // Ohne localStorage (z.B. privater Modus) bleibt es bei der Sprache dieser Sitzung.
    }
  }
}
